import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Link,
  Chip
} from '@mui/material';
import { apiClient } from '../../lib/apiClient';
import { formatDate } from '../../lib/dateUtils';
import { isYouTubeUrl, getYouTubeEmbedUrl } from '../../lib/videoUtils';
import NoChurchSelected from '../../components/common/NoChurchSelected';

function SermonsView({ user }) {
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get user's churches first to validate
        const myChurches = await apiClient.getMyChurches(user.id);
        
        if (myChurches.length === 0) {
          localStorage.removeItem('selectedChurch');
          setError('Please join a church first');
          setLoading(false);
          return;
        }

        // Get selected church from localStorage and validate it
        const stored = localStorage.getItem('selectedChurch');
        let church;
        if (stored) {
          const storedChurch = JSON.parse(stored);
          // Verify the stored church is in the user's churches
          const isValidChurch = myChurches.some(c => c.id === storedChurch.id);
          if (isValidChurch) {
            church = storedChurch;
          } else {
            // Invalid church, use first available
            localStorage.removeItem('selectedChurch');
            church = myChurches[0];
            localStorage.setItem('selectedChurch', JSON.stringify(church));
            window.dispatchEvent(new Event('churchSelected'));
          }
        } else {
          // No stored church, use first one
          church = myChurches[0];
          localStorage.setItem('selectedChurch', JSON.stringify(church));
          window.dispatchEvent(new Event('churchSelected'));
        }

        setSelectedChurch(church);
        const data = await apiClient.getSermons(church.id);
        // Debug: log first sermon's date to see its structure
        if (data.length > 0 && data[0].sermonDate) {
          console.log('Sermon date structure:', data[0].sermonDate, typeof data[0].sermonDate);
        }
        setSermons(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadData();
    }
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && error === 'Please join a church first') {
    return <NoChurchSelected user={user} />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        Sermons
      </Typography>
      {selectedChurch && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {selectedChurch.name}
        </Typography>
      )}

      {sermons.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No sermons available yet.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
          {sermons.map((sermon) => (
            <Card key={sermon.id}>
              <CardContent>
                <Typography variant="h6">{sermon.title}</Typography>
                {sermon.speakerName && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Speaker: {sermon.speakerName}
                  </Typography>
                )}
                {sermon.description && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {sermon.description}
                  </Typography>
                )}
                {sermon.videoUrl && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    {isYouTubeUrl(sermon.videoUrl) ? (
                      <Box
                        sx={{
                          position: 'relative',
                          paddingBottom: '56.25%', // 16:9 aspect ratio
                          height: 0,
                          overflow: 'hidden',
                          borderRadius: 1,
                        }}
                      >
                        <Box
                          component="iframe"
                          src={getYouTubeEmbedUrl(sermon.videoUrl)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 0,
                          }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </Box>
                    ) : (
                      <Box
                        component="video"
                        controls
                        src={sermon.videoUrl}
                        sx={{
                          width: '100%',
                          maxWidth: '100%',
                          borderRadius: 1,
                        }}
                      >
                        Your browser does not support the video tag.
                      </Box>
                    )}
                  </Box>
                )}
                <Box sx={{ mt: sermon.videoUrl ? 0 : 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {sermon.audioUrl && (
                    <Link href={sermon.audioUrl} target="_blank" rel="noopener noreferrer">
                      Listen to Audio
                    </Link>
                  )}
                  {sermon.duration > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Duration: {sermon.duration} minutes
                    </Typography>
                  )}
                </Box>
                {sermon.tags && sermon.tags.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {sermon.tags.map((tag, idx) => (
                      <Chip key={idx} label={tag} size="small" />
                    ))}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  {formatDate(sermon.sermonDate)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default SermonsView;

